export const SHARED_PACKAGE_VERSION = '0.0.0';

export type Role = 'contributor' | 'approver' | 'administrator';

/**
 * Whether an app-level role carries approver authority — `approver` or
 * `administrator`.
 *
 * Administrators are approvers-and-more everywhere in this product: they reach
 * every team's inbox, they approve, and a team edit never demotes them. So
 * "is an approver" is a two-member set, not an equality test, and it is exactly
 * the set `requireRole('approver')` gates on
 * (`packages/api/src/middleware/require-role.ts`, which calls this).
 *
 * This is the single source of truth for that set, and it lives in `shared`
 * because both `packages/api` and `packages/web` need it and neither may import
 * the other. Four call sites read it — the admin-category seed reconciler, the
 * entry-write guard, the demotion sweep, and the timesheet badge — and they
 * have to agree exactly. A seed that fans a row out to a role the guard then
 * refuses hours from strands the row; a sweep that disagrees with the seed
 * deletes a row the next read re-creates. Re-deriving
 * `role === 'approver' || role === 'administrator'` inline is how those four
 * drift apart one call site at a time, each copy locally correct in its own
 * diff (see
 * `docs/solutions/conventions/two-implementations-of-one-concept-diverge-at-the-edge-2026-09-08.md`).
 */
export function isApproverRole(role: Role): boolean {
  return role === 'approver' || role === 'administrator';
}

/**
 * Cognito identity-provider name for UT Enterprise Auth federation.
 *
 * Single-sourced because it is a security trust boundary: the CDK OIDC provider is created
 * with this exact name (infra/constructs/auth/oidc-provider.ts), and the API auth middleware
 * trusts a token as federated only when Cognito's `identities` claim references it
 * (packages/api/src/middleware/auth.ts). A drift between the two would silently break
 * federation — federated logins would stop being recognized and lose their EID identity.
 */
export const ENT_AUTH_PROVIDER_NAME = 'UtEntAuth';

/**
 * Error code returned with the 403 an authenticated-but-unprovisioned federated user gets.
 *
 * Single-sourced because it is a contract across three packages: the API returns it as
 * `{ code }` (packages/api/src/middleware/auth.ts), and the SPA keys its
 * "account not set up" card on it (packages/web/src/pages/NotProvisioned.tsx). A drift
 * would silently degrade that card back into a generic redirect loop, which is exactly
 * the broken-app experience the code exists to avoid.
 */
export const ACCOUNT_NOT_PROVISIONED = 'account_not_provisioned';

/**
 * Machine-readable code on the 409 returned when deactivating a line
 * classification would leave none active. Same reason as the code above:
 * callers branch on this rather than on the message text, so rewording the
 * message cannot change behaviour. The endpoint may grow other 409s.
 */
export const LAST_ACTIVE_CLASSIFICATION = 'last_active_classification';

/**
 * The three other 409s the line-classification endpoint can return, all about
 * `is_default` — the classification a new task starts on. Distinct codes rather
 * than one, because the remedy differs in each case and the UI says so:
 *
 * - LOCKED   — deactivating the current default. Set another default first.
 * - UNSET    — clearing the default outright. The default moves by being set
 *              elsewhere; the catalog is never left without one.
 * - INACTIVE — making an inactive classification the default. Reactivate first.
 */
export const DEFAULT_CLASSIFICATION_LOCKED = 'default_classification_locked';
export const DEFAULT_CLASSIFICATION_UNSET = 'default_classification_unset';
export const DEFAULT_CLASSIFICATION_INACTIVE = 'default_classification_inactive';

/**
 * Machine-readable code on every 409 that means "the owning project is
 * archived" — adding a task, a lead, a team, or an assignment to it.
 *
 * Needed because these endpoints already return 409 for unrelated reasons:
 * `POST /v1/tasks/:taskId/team-memberships` answers 409 both for an archived
 * project and for a team that is not on the project's palette. Without a code
 * the only way to tell them apart is matching English prose — which contains
 * typographic quotes and is expected to be reworded freely.
 */
export const PROJECT_ARCHIVED = 'project_archived';

/**
 * Sibling of {@link PROJECT_ARCHIVED} for the task-level case: the project is
 * live but this particular task has been archived. Distinct because the remedy
 * differs — the caller picks another task rather than restoring a project — and
 * because "that project is archived" would simply be untrue.
 */
export const TASK_ARCHIVED = 'task_archived';

/**
 * Machine-readable code on the 409 that means "what you were shown is no longer
 * true, so the choice you made from it is not safe to apply".
 *
 * Only raised for the one premise that silently changes a destructive decision:
 * releasing the project code having been told no hours were ever logged. Hours
 * arriving turns a free action into one that rewrites the code on every
 * historical report row — and the caution explaining that was never displayed,
 * because at render time there was nothing to caution about.
 *
 * Deliberately NOT raised when the observed minutes merely changed: a manager
 * who ticked the box with hours already showing has read the caution and
 * accepted it, and 409ing them because the total moved would be noise.
 */
export const PROJECT_IMPACT_CHANGED = 'project_impact_changed';

/**
 * Machine-readable code on the 409 refusing a write to an approver-only
 * admin category from someone whose role is neither approver nor administrator.
 *
 * Needed for the same reason as the codes above: the timesheet line and entry
 * endpoints already answer 409 for an inactive category, a lock cutoff that has
 * passed, an archived project and a closed reporting period. Without a code the
 * only way to tell them apart is matching English prose — and the prose here is
 * deliberately vague, since the refusal must not confirm to someone who guessed
 * a UUID that the category exists. The code carries the meaning so the message
 * does not have to.
 *
 * The remedy also differs: every other 409 on that endpoint has one the caller
 * can act on, and this one has none, so the UI explains rather than offering a
 * fix.
 */
export const CATEGORY_APPROVER_ONLY = 'category_approver_only';

/**
 * Sibling of {@link CATEGORY_APPROVER_ONLY} for a category assigned by leads:
 * the 409 refusing new hours on (or a new line for) a lead-assigned category
 * this person is not granted. Distinct because the remedy differs — the person
 * asks their lead, not an administrator — and because the copy names a lead's
 * decision rather than a role. The same vagueness rule applies: the message must
 * not confirm that a guessed category exists.
 *
 * Decreases still save (the ratchet); only new hours are refused.
 */
export const CATEGORY_NOT_GRANTED = 'category_not_granted';

/**
 * Machine-readable code on the 409 refusing a General activities grant write —
 * a person's individual choice or a team's "whole team" setting — on a category
 * that is not an ACTIVE category assigned by leads (visibility `everyone` or
 * `approvers`, or deactivated). Re-checked inside the write's transaction after
 * the category lock, so a grid left open while an administrator switched the
 * category cannot write inert grant rows or audit noise. The grid refetches.
 *
 * Locked categories stay grantable: a lock limits hours, not who has the row.
 */
export const CATEGORY_NOT_LEAD_ASSIGNED = 'category_not_lead_assigned';

/**
 * Machine-readable code on the 409 refusing to turn a team's "whole team"
 * setting off when it is not on — the heading the client clicked no longer
 * matches the server. Turning a team off is only offered from a fully-on
 * heading, or from the explicit "Turn off for {Team}…" action on a mixed team
 * whose whole-team setting is on; anything else means another lead changed the
 * team first. The grid refetches and says the team changed.
 */
export const TEAM_NOT_FULLY_ON = 'team_not_fully_on';

/**
 * Machine-readable code on the 409 that means "the impact you confirmed is no
 * longer what this write would do" for a General activities grant write. The
 * General activities sibling of {@link PROJECT_IMPACT_CHANGED}.
 *
 * Destructive confirms (team off, a person untick from the dialog) and a team
 * on that would override another lead's removals send back the sets they were
 * shown. The server recomputes them inside the write's transaction with the
 * same function the preview used; if they differ, nothing is written and the
 * 409 body carries a fresh preview (`GeneralActivityImpactChangedResponse`) for
 * the dialog to re-render and re-confirm. Two leads can act on the same person
 * concurrently, so this is an expected path, not an exotic one.
 */
export const GRANT_IMPACT_CHANGED = 'grant_impact_changed';

/**
 * Machine-readable code on the 409 refusing any write to a week inside a closed
 * reporting period — a contributor's edit, submit or recall, and an approver's or
 * administrator's approve, return or unapprove alike (decided 2026-10-19, #65).
 *
 * One code for every path because the remedy is the same everywhere: an
 * administrator reopens the period. The UI branches on this rather than on the
 * message, which differs per action ("Edits are not allowed", "Recall is not
 * allowed", …) and is free to be reworded.
 */
export const REPORTING_PERIOD_CLOSED = 'reporting_period_closed';

export * from './api/index.js';
export * from './environments.js';
export * from './hours.js';
